import { useState } from 'react'
import dynamic from 'next/dynamic'
import { useSelector } from 'react-redux'
import { useRouter } from 'next/router'
import { useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import InfiniteScroll from 'react-infinite-scroll-component'
import LazyLoad from 'react-lazyload'
import { flatten, max } from 'lodash-es'

import { selectWebview } from 'store/webview/webviewSlice'
import { useAlbums, useInfiniteAlbums } from 'utils/api/hooks'
import { useAlbumModal } from 'utils/hooks'
import AlbumListItem from 'components/common/AlbumListItem'
import Pagination from 'components/common/Pagination'
import { FullscreenModal } from 'components/common/Modal'
import { HideInMobileBox } from 'components/common/Hidden'
import { GtmAgent } from 'utils/hooks/analytics'

const DynamicAlbumContent = dynamic(() =>
  import('components/pages/Album/AlbumContent')
)

const NUMBER_PER_PAGE = 30

const Albums = ({
  venderId,
  containerId,
  isMobile,
  count,
  nativeHeaderControlHandler,
}) => {
  const [page, setPage] = useState(1)

  const { data: paginatedAlbums } = useAlbums(() => {
    if (isMobile) {
      return null
    }
    return {
      venderId,
      skip: max([page - 1, 0]) * NUMBER_PER_PAGE,
      limit: NUMBER_PER_PAGE,
    }
  })
  const { data: infiniteAlbums, size, setSize } = useInfiniteAlbums(() => {
    if (!isMobile) {
      return null
    }
    return { venderId, limit: NUMBER_PER_PAGE }
  })
  const albums = flatten(isMobile ? infiniteAlbums : paginatedAlbums)
  const onLoadMore = () => setSize(size + 1)

  const router = useRouter()

  const webview = useSelector(selectWebview)
  const albumModalOptions = {
    webview,
    nativeHeaderControlHandler,
  }
  const {
    albumModal,
    closeAlbumModal,
    onAlbumClick,
    onAlbumContentClose,
  } = useAlbumModal(router.asPath, router, albumModalOptions)

  const handleAlbumClick = (event, album) => {
    if (!album) {
      onAlbumClick(event, album)
      return
    }
    GtmAgent.directSendToGTM({
      event: 'living_action',
      living_event_name: 'booking_service_photoalbum',
      living_click_name: album?.name,
      living_click_location: '廠商頁',
      living_vender: album?.vender?.name,
      living_click_photoalbum_id: album?._id,
    })
    onAlbumClick(event, album)
  }

  const theme = useTheme()
  const isLgUp = useMediaQuery((theme) => theme.breakpoints.up('lg'))
  // 根據螢幕大小限制顯示的專輯數量
  const displayedAlbums = isLgUp ? albums.slice(0, 8) : albums.slice(0, 6)

  return (
    <>
      {/* <HideInMobileBox display="flex" justifyContent="center" mb={6}>
        <Pagination
          page={page}
          setPage={setPage}
          pageAmount={Math.floor(count / NUMBER_PER_PAGE) + 1}
        />
      </HideInMobileBox> */}

      <InfiniteScroll
        dataLength={albums?.length || 0}
        hasMore={true}
        next={onLoadMore}
        scrollableTarget={containerId}
      >
        <Box overflow="hidden">
          <Grid container spacing={{ xs: 4, lg: 6 }}>
            {displayedAlbums?.map((album) => (
              <Grid item xs={6} lg={3} key={album._id}>
                <LazyLoad
                  height={350}
                  offset={350}
                  scrollContainer={`#${containerId}`}
                >
                  <AlbumListItem
                    album={album}
                    onClick={handleAlbumClick}
                    usedInVenderPage={true}
                  />
                </LazyLoad>
                <noscript>
                  <style>{`.lazyload-placeholder { display: none; }`}</style>
                  <AlbumListItem
                    album={album}
                    onClick={handleAlbumClick}
                    usedInVenderPage={true}
                  />
                </noscript>
              </Grid>
            ))}
          </Grid>
        </Box>
      </InfiniteScroll>

      {/* <HideInMobileBox display="flex" justifyContent="center" mt={6}>
        <Pagination
          page={page}
          setPage={setPage}
          pageAmount={Math.floor(count / NUMBER_PER_PAGE) + 1}
        />
      </HideInMobileBox> */}

      {router?.isReady && albumModal.open && (
        <FullscreenModal open={albumModal.open} onClose={closeAlbumModal}>
          <DynamicAlbumContent
            onClose={onAlbumContentClose}
            closeModal={closeAlbumModal}
            fallbackData={albumModal.fallbackData}
          />
        </FullscreenModal>
      )}
    </>
  )
}

export default Albums
